'use client'

import Image from "next/image"
import { motion } from "framer-motion"
import { MapPin, Clock, ArrowDown, Sparkles, Handshake } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SITE_CONFIG, BOUTIQUE_EVA_DIAW, getTotalArticlesCount } from "@/lib/site-config"
import { OrnamentalDivider } from "./ornamental-divider"

export function HeroSection() {
  const totalArticles = getTotalArticlesCount()

  return (
    <section id="hero" className="relative min-h-[92vh] flex items-center overflow-hidden bg-pattern-islamic">
      {/* Decorative top ornament */}
      <div className="absolute top-0 left-0 right-0 h-1 gradient-gold" />
      <div className="absolute top-1 left-0 right-0 h-px bg-gold/50" />

      {/* Background facade image */}
      <div className="absolute inset-0 z-0">
        <Image
          src="/images/hero/supermarket-facade.png"
          alt="Façade SADIYA STYLE CUIR — Paris 10e"
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
        />
        {/* Layered overlays — keep the facade visible in the center, deepen edges for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-cuir/55 via-transparent to-cuir/55" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(13,8,4,0.55)_100%)]" />
        {/* Subtle arabesque overlay for tactile oriental feel */}
        <div className="absolute inset-0 overlay-arabesque opacity-40" />
      </div>

      {/* Background decorative arches */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-gold/10 blur-3xl animate-float" />
        <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-emerald/10 blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full border border-gold/60 bg-cuir/45 text-gold text-[11px] uppercase tracking-luxe-wide mb-8 backdrop-blur-md [text-shadow:0_1px_4px_rgba(0,0,0,0.85),0_0_12px_rgba(0,0,0,0.6)]"
          >
            <Sparkles className="h-3 w-3" />
            Maison de luxe depuis le cœur de Paris
          </motion.div>

          {/* Title — strengthened glassmorphism */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.05] mb-5"
          >
            <span className="inline-block px-8 py-3 rounded-2xl bg-cuir/55 backdrop-blur-xl border border-gold/50 shadow-luxe-deep">
              <span className="text-cuir tracking-luxe">SADIYA</span>{" "}
              <span className="text-gradient-gold tracking-luxe">STYLE CUIR</span>
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-2xl md:text-3xl text-emerald italic mb-9 tracking-luxe-wide"
          >
            <span className="inline-block px-6 py-2 rounded-full bg-cuir/35 backdrop-blur-md border border-gold/30">
              « {SITE_CONFIG.tagline} »
            </span>
          </motion.p>

          {/* Description — improved legibility */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-base md:text-lg text-white/95 leading-[1.8] mb-12 max-w-2xl mx-auto [text-shadow:0_1px_4px_rgba(0,0,0,0.95),0_2px_12px_rgba(0,0,0,0.75),0_0_24px_rgba(0,0,0,0.55)]"
          >
            Neuf rayons du monde réunis sous un même toit : trésors méditerranéens, soieries persanes,
            bazin sénégalais, dattes de Madinah, épices de Zanzibar, batik indonésien et créations
            afro-contemporaines. Une expérience de shopping d'exception.
          </motion.p>

          {/* CTA — luxury buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
          >
            <Button asChild size="lg" className="btn-gold-shimmer border border-gold/60 px-9 h-12 rounded-full font-medium tracking-luxe">
              <a href="#rayons">
                <span className="mr-2">✦</span>
                Explorer les 9 rayons · {totalArticles} articles
              </a>
            </Button>
            <Button asChild size="lg" className="btn-luxe-outline bg-cuir/30 backdrop-blur-md px-9 h-12 rounded-full font-medium tracking-luxe hover:bg-gold">
              <a href="#contact">Nous rendre visite</a>
            </Button>
          </motion.div>

          {/* Partnership banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-cuir/45 backdrop-blur-md border border-gold/40 mb-14 [text-shadow:0_1px_3px_rgba(0,0,0,0.85),0_0_12px_rgba(0,0,0,0.6)]"
          >
            <Handshake className="h-5 w-5 text-gold flex-shrink-0" />
            <div className="text-left">
              <span className="text-[11px] text-white/85 block leading-tight uppercase tracking-luxe">
                En collaboration avec la
              </span>
              <a
                href={BOUTIQUE_EVA_DIAW.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-display text-sm font-semibold text-gold hover:text-gold-soft transition-colors leading-tight"
              >
                {BOUTIQUE_EVA_DIAW.name}
                <span className="ml-2 text-[10px] text-gold-soft uppercase tracking-luxe-wide">
                  {BOUTIQUE_EVA_DIAW.domain}
                </span>
              </a>
            </div>
          </motion.div>

          {/* Location chips */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-sm text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.9),0_2px_10px_rgba(0,0,0,0.7),0_0_20px_rgba(0,0,0,0.5)]"
          >
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gold" />
              {SITE_CONFIG.address.line1} · {SITE_CONFIG.address.zip} {SITE_CONFIG.address.city}
            </span>
            <span className="hidden md:inline text-gold">✦</span>
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald" />
              {SITE_CONFIG.address.metro}
            </span>
            <span className="hidden md:inline text-gold">✦</span>
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-gold" />
              {SITE_CONFIG.contact.hours}
            </span>
          </motion.div>

          {/* Ornamental divider before scroll hint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-16 max-w-md mx-auto"
          >
            <OrnamentalDivider variant="star" />
          </motion.div>

          {/* Scroll hint */}
          <motion.a
            href="#rayons"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="inline-flex flex-col items-center gap-2 mt-8 text-gold hover:text-gold-soft transition-colors [text-shadow:0_1px_4px_rgba(0,0,0,0.85),0_2px_12px_rgba(0,0,0,0.65)]"
          >
            <span className="text-[11px] uppercase tracking-luxe-wide">Découvrir</span>
            <ArrowDown className="h-5 w-5 animate-bounce" />
          </motion.a>
        </div>
      </div>
    </section>
  )
}
